
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { 
  Calendar, 
  User, 
  Tag, 
  Globe, 
  Clock, 
  CheckCircle, 
  Activity,
  Zap,
  Shield,
  BarChart3,
  Copy,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react';
import type { CustomAction } from '@/types/action';

interface ActionOverviewTabProps {
  action: CustomAction;
}

export const ActionOverviewTab = ({ action }: ActionOverviewTabProps) => {
  const [expandedSections, setExpandedSections] = useState({
    performance: true,
    security: true,
    usage: true
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const copyActionId = () => {
    navigator.clipboard.writeText(action.id);
  };

  // Mock performance data
  const performanceMetrics = {
    successRate: 95,
    avgResponseTime: 245,
    totalCalls: 1247,
    lastWeekCalls: 89
  };

  return (
    <div className="space-y-8">
      {/* Hero Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="relative overflow-hidden bg-gradient-to-br from-blue-50/80 to-blue-100/60 backdrop-blur-sm border-blue-200/50 hover:shadow-lg transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 mb-1">Success Rate</p>
                <p className="text-3xl font-bold text-blue-900">{performanceMetrics.successRate}%</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <Progress value={performanceMetrics.successRate} className="mt-3 h-2" />
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden bg-gradient-to-br from-green-50/80 to-green-100/60 backdrop-blur-sm border-green-200/50 hover:shadow-lg transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 mb-1">Response Time</p>
                <p className="text-3xl font-bold text-green-900">{performanceMetrics.avgResponseTime}ms</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <Zap className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <p className="text-xs text-green-600 mt-2">Average response time</p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden bg-gradient-to-br from-purple-50/80 to-purple-100/60 backdrop-blur-sm border-purple-200/50 hover:shadow-lg transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600 mb-1">Total Calls</p>
                <p className="text-3xl font-bold text-purple-900">{performanceMetrics.totalCalls.toLocaleString()}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Activity className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-purple-600 mt-2">+{performanceMetrics.lastWeekCalls} this week</p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden bg-gradient-to-br from-orange-50/80 to-orange-100/60 backdrop-blur-sm border-orange-200/50 hover:shadow-lg transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600 mb-1">Status</p>
                <Badge 
                  variant={action.enabled ? 'default' : 'secondary'}
                  className={action.enabled 
                    ? 'bg-green-50 text-green-700 border-green-200 text-lg px-3 py-1' 
                    : 'bg-slate-50 text-slate-600 border-slate-200 text-lg px-3 py-1'
                  }
                >
                  {action.enabled ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Shield className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-8 lg:grid-cols-3">
        {/* Basic Information - Takes up 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-white/90 backdrop-blur-xl border-slate-200/50 shadow-xl hover:shadow-2xl transition-all duration-300">
            <CardHeader className="pb-6">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3 text-2xl font-bold text-slate-800">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/30 border border-blue-400/30 flex items-center justify-center">
                    <Tag className="h-6 w-6 text-blue-600" />
                  </div>
                  Action Details
                </CardTitle>
                <Button variant="outline" size="sm" onClick={copyActionId} className="gap-2">
                  <Copy className="h-4 w-4" />
                  Copy ID
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Action Name
                  </label>
                  <div className="p-4 rounded-xl bg-gradient-to-r from-slate-50/80 to-slate-100/60 border border-slate-200/50 hover:border-slate-300/50 transition-colors">
                    <p className="text-slate-900 font-semibold text-lg">{action.name}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Tool Name
                  </label>
                  <div className="p-4 rounded-xl bg-gradient-to-r from-slate-50/80 to-slate-100/60 border border-slate-200/50 hover:border-slate-300/50 transition-colors">
                    <p className="text-slate-800">{action.toolName || 'Not specified'}</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <label className="text-sm font-semibold text-slate-700">Description</label>
                <div className="p-4 rounded-xl bg-gradient-to-r from-slate-50/80 to-slate-100/60 border border-slate-200/50">
                  <p className="text-slate-800 leading-relaxed text-base">{action.description}</p>
                </div>
              </div>
              
              <Separator className="my-6" />
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-slate-700">Category</label>
                  <div className="flex">
                    <Badge variant="outline" className="bg-blue-50/80 border-blue-300/50 text-blue-700 hover:bg-blue-100/80 px-3 py-1 text-sm">
                      {action.category || 'Custom'}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-slate-700">HTTP Method</label>
                  <div className="flex">
                    <Badge variant="outline" className="font-mono bg-green-50/80 text-green-700 border-green-200/50 hover:bg-green-100/80 px-3 py-1 text-sm">
                      {action.method}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-slate-700">Availability</label>
                  <div className="flex">
                    <Badge 
                      variant={action.isActive ? 'default' : 'destructive'}
                      className={action.isActive 
                        ? 'bg-green-50/80 text-green-700 border-green-200/50 hover:bg-green-100/80 px-3 py-1' 
                        : 'bg-red-50/80 text-red-700 border-red-200/50 hover:bg-red-100/80 px-3 py-1'
                      }
                    >
                      {action.isActive ? 'Available' : 'Unavailable'}
                    </Badge>
                  </div>
                </div>
              </div>

              {action.tags && action.tags.length > 0 && (
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-slate-700">Tags</label>
                  <div className="flex flex-wrap gap-2">
                    {action.tags.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="bg-slate-100/80 text-slate-700 border-slate-200/50 hover:bg-slate-200/80 transition-colors">
                        #{tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Creator Information */}
          <Card className="bg-white/90 backdrop-blur-xl border-slate-200/50 shadow-xl hover:shadow-2xl transition-all duration-300">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 text-xl font-bold text-slate-800">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500/20 to-indigo-600/30 border border-indigo-400/30 flex items-center justify-center">
                  <User className="h-5 w-5 text-indigo-600" />
                </div>
                Creator Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6 p-6 rounded-xl bg-gradient-to-r from-indigo-50/80 to-purple-50/60 border border-indigo-200/40 hover:border-indigo-300/50 transition-all duration-300">
                <div className="h-16 w-16 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-xl">
                    {action.createdBy.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-bold text-slate-900 text-xl">{action.createdBy.name}</p>
                  <p className="text-sm text-slate-600 mb-2">Action Creator</p>
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Created {new Date(action.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="gap-2">
                  <ExternalLink className="h-4 w-4" />
                  View Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Timeline Card */}
          <Card className="bg-white/90 backdrop-blur-xl border-slate-200/50 shadow-xl hover:shadow-2xl transition-all duration-300">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 text-lg font-bold text-slate-800">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-purple-600/30 border border-purple-400/30 flex items-center justify-center">
                  <Clock className="h-4 w-4 text-purple-600" />
                </div>
                Timeline
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-r from-slate-50/50 to-slate-100/30 border border-slate-200/30 hover:border-slate-300/40 transition-colors">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Calendar className="h-5 w-5 text-green-600" />
                </div>
                <div className="space-y-1 flex-1">
                  <label className="text-sm font-semibold text-slate-700">Created</label>
                  <p className="text-sm text-slate-900 font-medium">
                    {new Date(action.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                  <p className="text-xs text-slate-500">
                    {new Date(action.createdAt).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-r from-slate-50/50 to-slate-100/30 border border-slate-200/30 hover:border-slate-300/40 transition-colors">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <div className="space-y-1 flex-1">
                  <label className="text-sm font-semibold text-slate-700">Last Updated</label>
                  <p className="text-sm text-slate-900 font-medium">
                    {new Date(action.updatedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                  <p className="text-xs text-slate-500">
                    {new Date(action.updatedAt).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Performance Insights */}
          <Card className="bg-white/90 backdrop-blur-xl border-slate-200/50 shadow-xl hover:shadow-2xl transition-all duration-300">
            <CardHeader className="pb-4">
              <button
                onClick={() => toggleSection('performance')}
                className="w-full flex items-center justify-between text-left"
              >
                <CardTitle className="flex items-center gap-3 text-lg font-bold text-slate-800">
                  <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-green-500/20 to-green-600/30 border border-green-400/30 flex items-center justify-center">
                    <BarChart3 className="h-4 w-4 text-green-600" />
                  </div>
                  Performance
                </CardTitle>
                {expandedSections.performance ? 
                  <ChevronUp className="h-4 w-4 text-slate-600" /> : 
                  <ChevronDown className="h-4 w-4 text-slate-600" />
                }
              </button>
            </CardHeader>
            {expandedSections.performance && (
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-600">Reliability</span>
                    <span className="text-sm font-bold text-green-600">{performanceMetrics.successRate}%</span>
                  </div>
                  <Progress value={performanceMetrics.successRate} className="h-2" />
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-600">Avg Response</span>
                    <span className="text-sm font-bold text-blue-600">{performanceMetrics.avgResponseTime}ms</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-600">Total Executions</span>
                    <span className="text-sm font-bold text-purple-600">{performanceMetrics.totalCalls}</span>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Quick Actions */}
          <Card className="bg-white/90 backdrop-blur-xl border-slate-200/50 shadow-xl hover:shadow-2xl transition-all duration-300">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 text-lg font-bold text-slate-800">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-orange-500/20 to-orange-600/30 border border-orange-400/30 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-orange-600" />
                </div>
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button variant="outline" className="w-full justify-start gap-2 hover:bg-slate-50">
                <Copy className="h-4 w-4" />
                Duplicate Action
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2 hover:bg-slate-50">
                <ExternalLink className="h-4 w-4" />
                View Documentation
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2 hover:bg-slate-50">
                <Activity className="h-4 w-4" />
                View Logs
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
